import { useCallback, useRef } from 'react';
import { useHover } from 'react-aria';
import { HStack, IconPlusFilled, type TestProps, Text } from '@direct-frontend/components';
import { noop, useToggle } from '@direct-frontend/stdlib';
import { Tooltip } from '../../../../ui/Tooltip';
import { rowDividerTestIds } from './testids';
import styles from './RowDivider.module.css';

interface RowDividerProps extends TestProps {
    newDisplaySequence: number;
    onClick: (display_sequence: number) => void;
    tooltipText?: string;
}

export const RowDivider = (props: RowDividerProps) => {
    const { newDisplaySequence, onClick, tooltipText, 'data-testid': dataTestId = rowDividerTestIds.root } = props;

    const onClickCallback = useCallback(() => {
        onClick(newDisplaySequence);
    }, [onClick, newDisplaySequence]);

    const iconRef = useRef(null);

    const { value: isOpen, toggleOn: open, toggleOff: close } = useToggle();

    const { hoverProps } = useHover({
        onHoverStart: () => {
            timerRef.current = window.setTimeout(() => {
                open();
            }, 500);
        },
        onHoverEnd: () => {
            window.clearTimeout(timerRef.current);
            close();
        },
    });

    const timerRef = useRef(0);

    return (
        <HStack className={styles.root} height="0px">
            <HStack
                className={styles.hoverWrapper}
                data-testid={rowDividerTestIds.hoverTarget}
                data-display-sequence={newDisplaySequence}
            >
                <div
                    className={styles.clickWrapper}
                    {...hoverProps}
                    onClick={isOpen ? onClickCallback : noop}
                    data-testid={dataTestId}
                    data-display-sequence={newDisplaySequence}
                >
                    <HStack className={styles.outerWrapper} width="max">
                        <HStack className={styles.iconWrapper} justifyContent="center">
                            <IconPlusFilled ref={iconRef} className={styles.icon} color="blue" size="16" role="main" />
                        </HStack>
                    </HStack>
                </div>
            </HStack>
            <Tooltip
                isOpen={Boolean(tooltipText) && isOpen}
                triggerRef={iconRef}
                customContent={
                    <Text size="s" color="current">
                        {tooltipText}
                    </Text>
                }
                color="info"
                placement="top"
            />
        </HStack>
    );
};
